import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

// Components
import { Button, Card, Empty, Icon, Input, Modal, Spin, Table, Tooltip } from 'antd';
import AddOrEditHomeModal from '../homes/AddOrEditHomeModal';

// Actions
import {
    fetchAdminHomes,
    handleDeleteHome,
    handleUndeleteHome,
    onAddOrEditModalIsEdit,
    onAddOrEditModalIsShown,
    onAddOrEditModalSelectedRow,
    onSearchHomes,
} from '../../data/homes/actions';

// Selectors
import {
    getAddOrEditHomeModalIsShown,
    getAllHomes,
    getAllHomesAreLoading,
    getSearchHomeFields,
} from '../../data/homes/selectors';
import { isRoleAdministrator } from '../../data/auth/selectors';

// Others
import { formatPattern, ROUTE_NAMES } from '../../utils';

const ButtonGroup = Button.Group;

class AllHomesTab extends PureComponent {
    state = {
        deleteModalShown: false,
        deleteModalIsLoading: false,
        recordToDelete: null,
    };

    componentWillMount() {
        const { fetchAdminHomes, searchHomeFields } = this.props;
        const { searchName, searchEmail, searchMeterId, searchCompany } = searchHomeFields;
        if (searchName  || searchEmail || searchMeterId || searchCompany) {
            this.handleSearch()
        } else {
            fetchAdminHomes();
        }
    }

    toggleAddOrEditModal = (isShown, record = undefined) => () => {
        const {
            onAddOrEditModalIsShown,
            onAddOrEditModalIsEdit,
            onAddOrEditModalSelectedRow,
        } = this.props;

        onAddOrEditModalIsShown(isShown);

        if (record) {
            onAddOrEditModalIsEdit(true);
            onAddOrEditModalSelectedRow(record);
        }
    };

    handleDetail = record => () => {
        const { push } = this.props;
        push(`${formatPattern(ROUTE_NAMES.HOME_DETAIL, { homeId: record.id })}?administrationView=true`);
    };

    onDeleteModalShownToggle = (isShown, record) => () => {
        this.setState({ deleteModalShown: isShown, recordToDelete: record || null });
    };

    onDeleteModalStateReset = () => {
        this.setState(() => ({
            deleteModalShown: false,
            deleteModalIsLoading: false,
            recordToDelete: null,
        }));
    };

    onDeleteHome = () => {
        this.setState({ deleteModalIsLoading: true });

        const { recordToDelete } = this.state;
        const { params, fetchAdminHomes, handleUndeleteHome, handleDeleteHome } = this.props;
        if (recordToDelete.deleted) {
            handleUndeleteHome(recordToDelete.home, params).then(() => {
                this.onDeleteModalStateReset();
                fetchAdminHomes(params);
            });
        } else {
            handleDeleteHome(recordToDelete.id, params).then(() => {
                this.onDeleteModalStateReset();
                fetchAdminHomes(params);
            });
        }
    };

    handlePageChange = (page, pageSize) => {
        const { fetchAdminHomes, params, adminHomes, searchHomeFields } = this.props;
        const { searchName, searchEmail, searchMeterId, searchCompany } = searchHomeFields;
        if (adminHomes && adminHomes.size > 0) {
            fetchAdminHomes(params, page - 1, pageSize, searchMeterId, searchName, searchEmail, searchCompany);
        }
    };

    handleSearch = () => {
        this.handlePageChange(1, 20);
    };

    handleShowSizeChange = (page, pageSize) => {
        const { fetchAdminHomes, params, adminHomes } = this.props;
        if (adminHomes && adminHomes.size > 0) {
            fetchAdminHomes(params, page - 1, pageSize);
        }
    };

    render() {
        const { adminHomes, adminHomesAreLoading, addOrEditModalIsShown, params, msgs, isAdmin, searchHomeFields, onSearchHomes } = this.props;
        const { searchName, searchEmail, searchMeterId, searchCompany } = searchHomeFields;
        const {
            deleteModalShown,
            deleteModalIsLoading,
            recordToDelete,
        } = this.state;

        const columns = [
            {
                title: msgs.id,
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: msgs.homes.name,
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: msgs.homes.owner,
                dataIndex: 'ownerUser',
                key: 'ownerUser',
                render: (text, record) => {
                    if (!record.ownerUser) return '-';
                    const name = record.ownerUser.name || '';
                    const surename = record.ownerUser.surename || '';
                    return !record.ownerUser.name && !record.ownerUser.surename
                        ? msgs.administration.invitationPending
                        : `${name} ${surename}`;
                },
            },
            {
                title: msgs.actionsLabel,
                key: 'action',
                className: 'text-right',
                width: 250,
                render: (text, record) => (
                    <span style={{ float: 'right' }}>
                        {!record.deleted && (
                            <ButtonGroup style={{ marginRight: 10 }}>
                                <Button type='primary' onClick={this.toggleAddOrEditModal(true, record)}>
                                    <Icon type='edit' />
                                </Button>
                            </ButtonGroup>
                        )}

                        {record.deleted ? (
                            <ButtonGroup>
                                <Button
                                    type='danger'
                                    ghost
                                    onClick={this.onDeleteModalShownToggle(true, record)}
                                >
                                    <Icon type='undo' /> {msgs.undelete}
                                </Button>
                            </ButtonGroup>
                        ) : (
                            <ButtonGroup style={{ marginRight: 10 }}>
                                <Button
                                    type='danger'
                                    onClick={this.onDeleteModalShownToggle(true, record)}
                                >
                                    <Icon type='delete' />
                                </Button>
                            </ButtonGroup>
                        )}
                        {!record.deleted && (
                            <Tooltip placement='right' title={msgs.homes.showHomeDetail}>
                                <Button onClick={this.handleDetail(record)} type='primary' ghost>
                                    {msgs.homes.detail.homeDetail}
                                    <Icon type='right' />
                                </Button>
                            </Tooltip>
                        )}
                    </span>
                ),
            },
        ];
        return (
            <React.Fragment>
                <Card
                    bodyStyle={{ padding: 0 }}
                    loading={adminHomesAreLoading}
                    title={
                        <span style={{ lineHeight: '42px' }}>
                            <Icon type='home' /> {msgs.homes.listOfHomes}
                        </span>
                    }
                    extra={
                        !adminHomesAreLoading && [
                            isAdmin
                                ? <>
                                    <span key='inp0'>Search:</span>
                                    <Input.Search key='inp1' placeholder='Meter ID' style={{ width: '120px', margin: '0 5px' }}
                                                  onChange={e => onSearchHomes({ searchMeterId: e.target.value, searchName: '', searchEmail: '', searchCompany: '' })}
                                                  defaultValue={searchMeterId} onSearch={this.handleSearch} type='number' />
                                    <Input.Search key='inp2' placeholder='Name' style={{ width: '150px', margin: '0 5px' }}
                                                  onChange={e => onSearchHomes({ searchMeterId: '', searchName: e.target.value, searchEmail, searchCompany: '' })}
                                                  defaultValue={searchName} onSearch={this.handleSearch} />
                                    <Input.Search key='inp3' placeholder='Email' style={{ width: '200px', margin: '0 5px' }}
                                                  onChange={e => onSearchHomes({ searchMeterId: '', searchName, searchEmail: e.target.value, searchCompany: '' })}
                                                  defaultValue={searchEmail} onSearch={this.handleSearch} />
                                    <Input.Search key='inp4' placeholder='Company' style={{ width: '150px', marginLeft: 5, marginRight: 10 }}
                                                  onChange={e => onSearchHomes({ searchMeterId: '', searchName: '', searchEmail: '', searchCompany: e.target.value })}
                                                  defaultValue={searchCompany} onSearch={this.handleSearch}/>
                                </>
                                : null,
                            <Button
                                key='add-home'
                                size='large'
                                type='primary'
                                onClick={this.toggleAddOrEditModal(true)}
                            >
                                <Icon type='plus' />
                                {msgs.homes.addHome}
                            </Button>,
                        ]
                    }
                >
                    {adminHomes && adminHomes.size > 0 && adminHomes.get('content').size > 0 ? (
                        <Table
                            pagination={{
                                showSizeChanger: true,
                                defaultPageSize:
                                    adminHomes && adminHomes.size > 0 ? adminHomes.get('size') : 20,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                pageSize:
                                    adminHomes && adminHomes.size > 0 ? adminHomes.get('size') : 20,
                                current:
                                    adminHomes && adminHomes.size > 0 ? adminHomes.get('number') + 1 : 1,
                                total:
                                    adminHomes && adminHomes.size > 0
                                        ? adminHomes.get('totalElements')
                                        : 0,
                                onChange: this.handlePageChange,
                                onShowSizeChange: this.handleShowSizeChange,
                            }}
                            rowClassName={record => {
                                if (record.deleted) {
                                    return 'ant-table-deleted-row';
                                }
                            }}
                            dataSource={
                                adminHomes && adminHomes.size > 0 && adminHomes.get('content').size > 0
                                    ? adminHomes.get('content').toJS()
                                    : []
                            }
                            columns={columns}
                        />
                    ) : (
                        <Empty
                            description={<span>{msgs.noDataAvailable}</span>}
                            style={{ margin: '30px auto' }}
                        />
                    )}
                </Card>
                {addOrEditModalIsShown && (
                    <AddOrEditHomeModal
                        isUsedInAdmin
                        msgs={msgs}
                        isShown={addOrEditModalIsShown}
                        params={params}
                        isAdmin
                        onCancel={this.toggleAddOrEditModal(false)}
                    />
                )}
                {recordToDelete && (
                    <Modal
                        key='delete-home-modal'
                        visible={deleteModalShown}
                        onCancel={this.onDeleteModalStateReset}
                        onOk={this.onDeleteHome}
                        okText={recordToDelete.deleted ? msgs.undelete : msgs.delete}
                        okType={recordToDelete.deleted ? 'primary' : 'danger'}
                        confirmLoading={deleteModalIsLoading}
                        cancelText={msgs.cancel}
                        title={
                            recordToDelete.deleted ? msgs.sureToUndeleteTitle : msgs.sureToDeleteTitle
                        }
                    >
                        <Spin spinning={deleteModalIsLoading} size='large'>
                            <div>
                                <p>
                                    {recordToDelete.deleted
                                        ? msgs.homes.sureToUndeleteContent
                                        : msgs.homes.sureToDeleteContent}{' '}
                                    <strong>{recordToDelete.name}</strong>?
                                </p>
                            </div>
                        </Spin>
                    </Modal>
                )}
            </React.Fragment>
        );
    }
}

export default connect(
    (state, ownProps) => ({
        ...ownProps,
        addOrEditModalIsShown: getAddOrEditHomeModalIsShown(state),
        adminHomes: getAllHomes(state),
        adminHomesAreLoading: getAllHomesAreLoading(state),
        isAdmin: isRoleAdministrator(state),
        searchHomeFields: getSearchHomeFields(state),
    }),
    {
        push,
        fetchAdminHomes,
        onAddOrEditModalIsShown,
        handleDeleteHome,
        handleUndeleteHome,
        onAddOrEditModalIsEdit,
        onAddOrEditModalSelectedRow,
        onSearchHomes,
    },
)(AllHomesTab);
